/**
 * The reverse channel: a tap on the board becomes the visitor's next message.
 *
 * reagent has no A2UI ingress and will not grow one — renderer→agent here is a
 * NEW RUN. So the bridge is small and its whole content is one promise:
 *
 *   `action.event.userMessage` is sent BYTE FOR BYTE as the visitor's message.
 *
 * That promise used to live in an agreement between `Chips.tsx` and `tools.py`,
 * where three separate readers keyed off the exact string (the lexical router,
 * the consent belts, the reverse parsers) and nothing checked they still agreed.
 * On this wire it is DECLARED — the spec's own field, with the meaning the spec
 * gives it — so a renderer that has never read our code sends the right string.
 * Nothing here normalises, completes, or auto-sends it.
 *
 * The whole `action` also travels as `context.a2ui_action`, for later: «which
 * card was tapped» is context the graph does not read yet and will.
 */

import { send } from '../lib/send';

/** `common_types.json#/$defs/Action`, the agent-event half. */
export type A2uiAction = {
  event: {
    name: string;
    userMessage?: unknown;
    context?: Record<string, unknown>;
  };
};

export type TapPayload = {
  /** Sent verbatim as the visitor's message. */
  userMessage: string;
  /** The action as it arrived, for `context.a2ui_action`. */
  action: Record<string, unknown>;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Reads a `Button.action` into something tappable, or `null`.
 *
 * The catalogue types `action` as an open object (its shape is the protocol's,
 * not ours), so the narrowing happens here, at the point of use, rather than
 * being re-declared in the generated schemas.
 *
 * `userMessage` is a `DynamicString` in the spec — it may be a literal or a
 * data-model path. **A path is refused rather than resolved**: what a tap sends
 * is load-bearing, and resolving one here would be this frontend deciding what
 * the visitor said. We never emit that form.
 */
export function readAction(action: unknown, sourceComponentId: string): TapPayload | null {
  if (!isRecord(action)) return null;
  const event = action.event;
  if (!isRecord(event)) return null;
  const message = event.userMessage;
  if (typeof message !== 'string' || message.trim() === '') {
    if (message !== undefined) {
      // eslint-disable-next-line no-console
      console.warn('[a2ui] a tap carried a non-literal userMessage and was ignored', {
        sourceComponentId,
        message,
      });
    }
    return null;
  }
  return { userMessage: message, action: action as Record<string, unknown> };
}

/**
 * Dispatches a tap. The transport adds `context.a2ui_action` from the payload
 * the caller keeps, so the graph gets both halves: the sentence and its origin.
 */
export function dispatchTap(payload: TapPayload, sourceComponentId: string, surfaceId: string | null): void {
  lastTap = {
    name: String((payload.action.event as Record<string, unknown>)?.name ?? 'tap'),
    surfaceId,
    sourceComponentId,
    timestamp: new Date().toISOString(),
    ...payload.action,
  };
  // Byte for byte. No trimming, no completion, no normalisation.
  send(payload.userMessage);
}

/**
 * The action behind the message currently being sent.
 *
 * A module-level slot rather than a parameter threaded through `send()`: the
 * composer, a chip and a card button all reach the same sender, and only one of
 * them has an action. `takeLastTap()` consumes it, so a typed message that
 * follows a tap cannot inherit the tap's context.
 */
let lastTap: Record<string, unknown> | null = null;

export function takeLastTap(): Record<string, unknown> | null {
  const value = lastTap;
  lastTap = null;
  return value;
}

/**
 * The name a START BUTTON's tap carries (#142). Not an A2UI action — the button is
 * the frontend's own, drawn before the board has anything — so it never travels as
 * `context.a2ui_action` (`tapContext`).
 */
export const STARTER_TAP = 'starter';

/**
 * Sends a start button's line and says WHICH button it was: `starter` is the id of
 * the questionnaire it opens (`phrases.json`, exported from the graph). The line
 * goes byte for byte, like any tap; the key rides the same one-shot slot a board
 * tap does, so a typed message that follows cannot inherit it. → whether the line was TAKEN
 * (`lib/send.ts`; `false` — a turn is running, offline): a card button that shows its own
 * state for the press (INV-PH-140) must not for one that went nowhere.
 */
export function dispatchStarter(userMessage: string, starter: string): boolean {
  lastTap = { name: STARTER_TAP, starter };
  return send(userMessage);
}

/**
 * The name a LINK's action carries (INV-PH-166): a saved plan's link was opened (`export/returnLink.ts`). Like the
 * start button it is the frontend's own, not an A2UI action: it travels as `context.trip_link` to the graph that
 * rebuilds the plan from it (`sessionStore`-free `graphFamily.readsTripLink`), and to no other.
 */
export const TRIP_LINK_TAP = 'trip_link';

/**
 * Sends the line of an opened link and says WHICH plan: the token is the graph's own (`peterhof_v2.return_link`),
 * passed through untouched. The line is only the visitor's bubble; code reads the token. → whether the line was
 * TAKEN (`lib/send.ts`).
 */
export function dispatchTripLink(userMessage: string, token: string): boolean {
  lastTap = { name: TRIP_LINK_TAP, token };
  const taken = send(userMessage);
  if (!taken) lastTap = null;
  return taken;
}

/**
 * The run-context keys a tap adds (`App.dispatch`). A start button → `starter`, and
 * only for a graph that reads it (`sessionStore.readsStarter`): every other graph's
 * context keeps the shape it had (an unknown key moves some of them onto another
 * `Context` construction — asst-peterhof-guide#139/#140). Any other tap →
 * `a2ui_action`, as before.
 */
export function tapContext(
  tap: Record<string, unknown> | null,
  readsStarter: boolean,
  readsTripLink = false,
): { starter?: string; a2ui_action?: Record<string, unknown>; trip_link?: { token: string } } {
  if (!tap) return {};
  if (isTripLinkTap(tap)) return readsTripLink ? { trip_link: { token: String(tap.token) } } : {};
  if (isStarterTap(tap)) {
    const starter = typeof tap.starter === 'string' ? tap.starter : '';
    return starter && readsStarter ? { starter } : {};
  }
  // A board button that is a start button too (#154: «Подготовить поездку» under the
  // highlights) carries the key in A2UI's own `event.context` — sent as `starter`
  // beside the action, to the graph that reads it.
  const event = tap.event;
  const context = event && typeof event === 'object' ? (event as Record<string, unknown>).context : undefined;
  const starter =
    context && typeof context === 'object' && typeof (context as Record<string, unknown>).starter === 'string'
      ? String((context as Record<string, unknown>).starter)
      : '';
  return { a2ui_action: tap, ...(starter && readsStarter ? { starter } : {}) };
}

/**
 * A start button's tap, told apart from a board tap by SHAPE, not by name alone: a
 * board tap always carries the A2UI `event` (`readAction` refuses one without it),
 * so a card action that happened to be named «starter» still travels as
 * `a2ui_action`.
 */
function isStarterTap(tap: Record<string, unknown>): boolean {
  return tap.name === STARTER_TAP && !('event' in tap) && typeof tap.starter === 'string';
}

/** Told apart from a board tap by shape, like the start button: no `event`, and a token. */
function isTripLinkTap(tap: Record<string, unknown>): boolean {
  return tap.name === TRIP_LINK_TAP && !('event' in tap) && typeof tap.token === 'string';
}
